// account-management-admin/src/src/components/Dashboard.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { FeedbackSubmissionsSection } from './FeedbackSubmissionsSection';
import { FeedbackPromptResponsesSection } from './FeedbackPromptResponsesSection';
import { FeedbackInsightsSection } from './FeedbackInsightsSection';
import { ContentReportsSection } from './ContentReportsSection';
import { UserInviteManagerSection } from './UserInviteManagerSection';
import { WaitlistSection } from './WaitListSection';

import { MetricsSection } from './MetricsSection';
import { OfficialAccountsSection } from './OfficialAccountsSection';
import { CampaignLinksSection } from './CampaignLinksSection';
import { NewsletterSection } from './NewsletterSection';
import { AccountManagementSection } from './AccountManagementSection';
import { AppSuggestionsSection } from './AppSuggestionsSection';
import { HelpCenterSection } from './HelpCenterSection';
import { AnalyticsReportSection } from './AnalyticsReportSection';

// Determine API base URL based on the current domain
const getApiBase = () => {
    const hostname = window.location.hostname;
    // If accessing from prod domain, use prod API
    if (hostname === 'admin.eluum.com') {
        return 'https://api.eluum.com/api';
    }
    // Otherwise use dev API (includes admin-dev.eluum.com and localhost)
    return 'https://api-dev.eluum.com/api';
};

const API_BASE = getApiBase();

// Keyed off the admin UI's own hostname rather than reading NODE_ENV so a
// developer running the admin shell against the prod API locally would still
// get the prod-filtered view. Matches the same hostname check getApiBase uses.
const IS_PROD = window.location.hostname === 'admin.eluum.com';

// help-center-service lives on its own standalone HTTP API, not the shared one
// above — HttpApiStack was already at CloudFormation's 500-resource-per-stack
// limit (see cdk/lib/help-center-stack.ts), so its routes never got registered
// there.
const getHelpCenterApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'admin.eluum.com') {
        return 'https://REPLACE_WITH_HELP_CENTER_API_ENDPOINT_PROD/api';
    }
    return 'https://f9mqw0r329.execute-api.us-west-2.amazonaws.com/api';
};

const HELP_CENTER_API_BASE = getHelpCenterApiBase();

export const Dashboard = () => {
    const navigate = useNavigate();
    const [addEmail, setAddEmail] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [removeEmail, setRemoveEmail] = useState('');
    const [addStatus, setAddStatus] = useState<string | null>(null);
    const [inviteStatus, setInviteStatus] = useState<string | null>(null);
    const [removeStatus, setRemoveStatus] = useState<string | null>(null);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [loadingInvite, setLoadingInvite] = useState(false);
    const [loadingRemove, setLoadingRemove] = useState(false);
    const [admins, setAdmins] = useState<string[]>([]);
    const [loadingAdmins, setLoadingAdmins] = useState(false);
    const [adminError, setAdminError] = useState<string | null>(null);
    const [showAdmins, setShowAdmins] = useState(false);  // State to control when to load admins
    const [massActionStatus, setMassActionStatus] = useState<string>('Unreviewed');
    const [massActionLoading, setMassActionLoading] = useState(false);
    const [massActionError, setMassActionError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'general' | 'account management'>('general');

    useEffect(() => {
        const rawUser = localStorage.getItem('user');
        const idToken = localStorage.getItem('idToken');

        try {
            const user = rawUser ? JSON.parse(rawUser) : null;
            const isAdmin = Boolean(user?.groups?.includes('Administrators'));

            if (!idToken || !isAdmin) {
                navigate('/');
                return;
            }

            const decoded = jwtDecode<{ exp: number }>(idToken);
            if (Date.now() >= decoded.exp * 1000) {
                // Token expired
                navigate('/');
            }
        } catch {
            navigate('/');
        }
    }, [navigate]);

    const authHeaders = () => {
        const idToken = localStorage.getItem('idToken');
        console.log('Authorization Token:', idToken);
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
        };
    };

    const handleAddToAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddStatus(null);

        if (!addEmail) {
            setAddStatus('Please enter an email.');
            return;
        }

        setLoadingAdd(true);
        try {
            const res = await fetch(`${API_BASE}/admin/add-to-admins`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ email: addEmail, groupName: 'Administrators' }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to add user to Administrators.');
            }

            const data = await res.json();
            setAddStatus(`Success: ${data.message}`);
            setAddEmail('');
        } catch (err: any) {
            setAddStatus(err.message || 'Failed to add user.');
        } finally {
            setLoadingAdd(false);
        }
    };
    const handleRemoveFromAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setRemoveStatus(null);

        if (!removeEmail) {
            setRemoveStatus('Please enter an email.');
            return;
        }

        setLoadingRemove(true);
        try {
            const res = await fetch(`${API_BASE}/admin/remove-from-admins`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ email: removeEmail, groupName: 'Administrators' }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to remove user from Administrators.');
            }

            const data = await res.json();
            setRemoveStatus(`Success: ${data.message}`);
            setRemoveEmail('');
        } catch (err: any) {
            setRemoveStatus(err.message || 'Failed to remove user.');
        } finally {
            setLoadingRemove(false);
        }
    };
    const fetchAdmins = async () => {
        setLoadingAdmins(true);
        setAdminError(null);

        try {
            const res = await fetch(`${API_BASE}/admin/list-admins`, {
                method: 'GET',
                headers: authHeaders(),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch admins.');
            }

            const data = await res.json();
            setAdmins(data.administrators?.map((admin: { email: string }) => admin.email) || []);
        } catch (err: any) {
            setAdminError(err.message || 'Error retrieving admin list.');
        } finally {
            setLoadingAdmins(false);
        }
    };

    const handleShowAdmins = () => {
        // Trigger the fetching of admins when button is clicked
        if (!showAdmins) {
            fetchAdmins();
        }
        // Toggle the state to show or hide the admins list
        setShowAdmins((prev) => !prev);
    };

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setInviteStatus(null);

        if (!inviteEmail) {
            setInviteStatus('Please enter an email.');
            return;
        }

        setLoadingInvite(true);
        try {
            const res = await fetch(`${API_BASE}/invite`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ email: inviteEmail }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to send invite.');
            }

            const data = await res.json();
            const maybeCode = data?.inviteCode ? ` Invite code: ${data.inviteCode}` : '';
            setInviteStatus(`Success: ${data.message}.${maybeCode}`);
            setInviteEmail('');
        } catch (err: any) {
            setInviteStatus(err.message || 'Failed to send invite.');
        } finally {
            setLoadingInvite(false);
        }
    };

    return (
        <div className="dashboard-page">
            <h1>Admin Dashboard</h1>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0' }}>
                {(['general', 'account management'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '0.6rem 1.4rem',
                            border: 'none',
                            borderBottom: activeTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
                            background: 'none',
                            cursor: 'pointer',
                            fontWeight: activeTab === tab ? 700 : 400,
                            color: activeTab === tab ? '#4f46e5' : '#555',
                            marginBottom: '-2px',
                            fontSize: '0.95rem',
                            textTransform: 'capitalize',
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>
            {activeTab === 'account management' ? (
                <main className="dashboard-grid">
                    <AccountManagementSection API_BASE={API_BASE} authHeaders={authHeaders} />
                </main>
            ) : (
            <main className="dashboard-grid">
            <section className="dashboard-card">
                <h2>Add user to Administrators</h2>
                <form onSubmit={handleAddToAdmin}>
                    <div className="form-group">
                        <label htmlFor="add-email">User email</label>
                        <input
                            id="add-email"
                            type="email"
                            value={addEmail}
                            onChange={(e) => setAddEmail(e.target.value)}
                            className="dashboard-input"
                            placeholder="user@example.com"
                        />
                    </div>
                    <button type="submit" disabled={loadingAdd} className="btn btn-primary">
                        {loadingAdd ? 'Adding...' : 'Add to Administrators'}
                    </button>
                </form>
                {addStatus && <p className="status">{addStatus}</p>}
            </section>
            <section className="dashboard-card">
                <h2>List of Administrators</h2>
                <button onClick={handleShowAdmins} className="btn btn-primary">
                    {showAdmins ? 'Hide Administrators' : 'View Administrators'}
                </button>
                {showAdmins && (
                    <div>
                        {loadingAdmins && <p className="status">Loading administrators...</p>}
                        {adminError && <p className="status status-error">{adminError}</p>}
                        {!loadingAdmins && !adminError && (
                            <ul>
                                {admins.length > 0 ? (
                                    admins.map((adminEmail, idx) => (
                                        <li key={idx}>{adminEmail}</li>
                                    ))
                                ) : (
                                    <p className="status">No administrators found.</p>
                                )}
                            </ul>
                        )}
                    </div>
                )}
            </section>
            <section className="dashboard-card">
                <h2>Remove user from Administrators</h2>
                <form onSubmit={handleRemoveFromAdmin}>
                    <div className="form-group">
                        <label htmlFor="remove-email">User email</label>
                        <input
                            id="remove-email"
                            type="email"
                            value={removeEmail}
                            onChange={(e) => setRemoveEmail(e.target.value)}
                            className="dashboard-input"
                            placeholder="user@example.com"
                        />
                    </div>
                    <button type="submit" disabled={loadingRemove} className="btn btn-danger">
                        {loadingRemove ? 'Removing...' : 'Remove from Administrators'}
                    </button>
                </form>
                {removeStatus && <p className="status">{removeStatus}</p>}
            </section>
            <section className="dashboard-card">
                <h2>Send invite</h2>
                <form onSubmit={handleSendInvite}>
                    <div className="form-group">
                        <label htmlFor="invite-email">Invite email</label>
                        <input
                            id="invite-email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="dashboard-input"
                            placeholder="invitee@example.com"
                        />
                    </div>
                    <button type="submit" disabled={loadingInvite} className="btn btn-primary">
                        {loadingInvite ? 'Sending...' : 'Send invite'}
                    </button>
                </form>
                {inviteStatus && <p className="status">{inviteStatus}</p>}
            </section>
            {/* Metrics section above bug reports */}
            <MetricsSection API_BASE={API_BASE} authHeaders={authHeaders} isProd={IS_PROD} />
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <FeedbackInsightsSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <ContentReportsSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <FeedbackSubmissionsSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <FeedbackPromptResponsesSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2'}}>
                <UserInviteManagerSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <WaitlistSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <OfficialAccountsSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <CampaignLinksSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <NewsletterSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <AppSuggestionsSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <HelpCenterSection
                    API_BASE={HELP_CENTER_API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            <section className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                <AnalyticsReportSection
                    API_BASE={API_BASE}
                    authHeaders={authHeaders}
                />
            </section>
            </main>
            )}
        </div>
    );
};

